import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import SiteHeader from '../components/SiteHeader';
import Footer from '../components/Footer';
import useAuthModal from '../hooks/useAuthModal';
import { fetchGhostPostBySlug, fetchGhostPosts } from '../utils/ghost';
import '../styles/blog-article.css';

const TAG_COLOR_CLASSES = ['article-tag-orange', 'article-tag-green', 'article-tag-blue'];

function getInitials(name) {
  return (name || 'Author')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

function formatPublishedDate(dateValue) {
  if (!dateValue) return '';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(dateValue));
}

export default function BlogArticleScreen() {
  const { slug } = useParams();
  const authModal = useAuthModal();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!slug) return;

    const controller = new AbortController();

    const loadArticle = async () => {
      setIsLoading(true);
      setError('');

      try {
        const currentPost = await fetchGhostPostBySlug(slug, controller.signal);
        if (!currentPost) {
          setError('Article not found.');
          setPost(null);
          return;
        }

        setPost(currentPost);

        const firstTagSlug = currentPost.tags?.[0]?.slug;
        const relatedFilter = firstTagSlug ? `tag:${firstTagSlug}` : '';
        const related = await fetchGhostPosts({ limit: 4, filter: relatedFilter }, controller.signal);
        setRelatedPosts(related.filter((candidate) => candidate.slug !== currentPost.slug).slice(0, 3));
      } catch (loadError) {
        setError(loadError.message || 'Failed to load article.');
      } finally {
        setIsLoading(false);
      }
    };

    loadArticle();

    return () => controller.abort();
  }, [slug]);

  const author = post?.authors?.[0];
  const publishedDate = useMemo(() => formatPublishedDate(post?.published_at), [post?.published_at]);
  const sanitizedHtml = useMemo(() => DOMPurify.sanitize(post?.html ?? ''), [post?.html]);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const twitterShare = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post?.title || '')}`;
  const linkedinShare = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;

  const copyLink = async (event) => {
    event.preventDefault();
    if (!navigator?.clipboard || !shareUrl) return;
    await navigator.clipboard.writeText(shareUrl);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SiteHeader authModal={authModal} />

      {isLoading && <p className="article-status">Loading article...</p>}
      {!isLoading && error && <p className="article-status">{error}</p>}

      {!isLoading && !error && post && (
        <>
          <nav className="article-breadcrumbs">
            <Link to="/">Home</Link>
            <span>&gt;</span>
            <Link to="/blog">Blog</Link>
            <span>&gt;</span>
            <span className="current">{post.title}</span>
          </nav>

          <header className="article-header">
            <div className="article-tags">
              {(post.tags || []).slice(0, 3).map((tag, index) => (
                <Link
                  to="/blog"
                  key={tag.id || tag.slug || tag.name}
                  className={`article-tag ${TAG_COLOR_CLASSES[index % TAG_COLOR_CLASSES.length]}`}
                >
                  {tag.name}
                </Link>
              ))}
            </div>

            <h1 className="article-title">{post.title}</h1>

            <p className="article-subtitle">{post.custom_excerpt || post.excerpt}</p>

            <div className="article-meta">
              <div className="article-author">
                <div className="article-author-avatar">{getInitials(author?.name)}</div>
                <div className="article-author-info">
                  <span className="article-author-name">{author?.name || 'Ghost Author'}</span>
                  <span className="article-author-role">{author?.bio || 'Author'}</span>
                </div>
              </div>
              <div className="article-meta-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                {publishedDate}
              </div>
              <div className="article-meta-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                {post.reading_time || 1} min read
              </div>
            </div>
          </header>

          <div className="article-hero">
            <div
              className="article-hero-image"
              style={post.feature_image ? {
                backgroundImage: `linear-gradient(135deg, rgba(30, 58, 95, 0.45) 0%, rgba(45, 74, 111, 0.8) 100%), url(${post.feature_image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              } : undefined}
            />
          </div>

          <article className="article-content">
            <div className="article-body" dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />

            <div className="article-share-section">
              <span className="article-share-label">Share this article</span>
              <div className="article-share-buttons">
                <a href={twitterShare} target="_blank" rel="noreferrer" className="article-share-btn article-share-twitter">X</a>
                <a href={linkedinShare} target="_blank" rel="noreferrer" className="article-share-btn article-share-linkedin">in</a>
                <a href="#" onClick={copyLink} className="article-share-btn article-share-copy">Copy</a>
              </div>
            </div>

            {author && (
              <div className="article-author-bio">
                <div className="article-author-bio-avatar">{getInitials(author.name)}</div>
                <div className="article-author-bio-content">
                  <h4>{author.name}</h4>
                  <p className="article-author-bio-role">{author.website || 'Ghost Author'}</p>
                  <p>{author.bio || 'Writes about interviews, careers and job search strategies.'}</p>
                </div>
              </div>
            )}
          </article>

          {relatedPosts.length > 0 && (
            <section className="article-related-section">
              <h2 className="article-related-title">Related Articles</h2>
              <div className="article-related-grid">
                {relatedPosts.map((relatedPost, index) => (
                  <Link to={`/blog/${relatedPost.slug}`} className="article-related-card" key={relatedPost.id}>
                    <div
                      className={`article-related-image ${relatedPost.feature_image ? '' : `article-related-gradient-${(index % 3) + 1}`}`.trim()}
                      style={relatedPost.feature_image ? {
                        backgroundImage: `linear-gradient(135deg, rgba(30, 58, 95, 0.4) 0%, rgba(45, 74, 111, 0.65) 100%), url(${relatedPost.feature_image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      } : undefined}
                    />
                    <div className="article-related-content">
                      <span className="article-related-tag">{relatedPost.tags?.[0]?.name || 'Article'}</span>
                      <h3 className="article-related-card-title">{relatedPost.title}</h3>
                      <span className="article-related-meta">{relatedPost.reading_time || 1} min read</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <section className="article-newsletter">
            <div className="article-newsletter-container">
              <h2>Get Interview Tips Weekly</h2>
              <p>Join 5,000+ developers who receive our best career advice every Thursday.</p>
              <form className="article-newsletter-form">
                <input
                  type="email"
                  className="article-newsletter-input"
                  placeholder="Your email"
                />
                <button type="submit" className="article-newsletter-btn">
                  Subscribe
                </button>
              </form>
            </div>
          </section>
        </>
      )}

      <Footer />
    </div>
  );
}
